import CoverageNotice from "./CoverageNotice";
import { useEffect, useMemo, useState } from "react";
import { Camera, VideoCamera, FileText, Microphone, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ExplorerTable } from "@/components/service/ExplorerTable";
import { DlMediaViewer, Thumb } from "@/components/dailylogs/DlMediaViewer";
import { RangePills, useRange } from "@/components/dailylogs/OverviewBoard";
import { PhotoActivity } from "@/components/dailylogs/PhotoActivity";

const pad = (n) => String(n).padStart(2, "0");
export const todayNY = () => { const d = new Date(new Date().toLocaleString("en-US", { timeZone: "America/New_York" })); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET" : "");
const ICON = { image: Camera, video: VideoCamera, audio: Microphone, document: FileText, ptt: Microphone };

export { OverviewBoard as OverviewTab } from "@/components/dailylogs/OverviewBoard";

export function ActivityTab() {
  const [view, setView] = useState("photos");
  return (
    <div className="space-y-3">
      <div className="inline-flex rounded-full border border-slate-200 p-0.5" data-testid="dl-activity-view">
        {[["photos", "Photos by site & crew"], ["messages", "All messages"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === k ? "bg-[#327de3] text-white" : "text-slate-600"}`} data-testid={`dl-activity-view-${k}`}>{l}</button>
        ))}
      </div>
      {view === "photos" ? <PhotoActivity /> : <MessageActivity />}
    </div>
  );
}

function MessageActivity() {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [rows, setRows] = useState([]);
  const [viewer, setViewer] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/dailylogs/messages", { params: { q, date_from: from || undefined, date_to: to || undefined } }).then(({ data }) => setRows(data)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q, from, to]);
  const columns = useMemo(() => [
    { key: "ts", label: "Sent", width: 150, render: (r) => fmt(r.sent_at) },
    { key: "project_name", label: "Project", width: 140 },
    { key: "group_name", label: "Group", width: 160 },
    { key: "sender_name", label: "Posted by", width: 130 },
    { key: "body", label: "Message", width: 360, render: (r) => <span title={r.body}>{r.body || <span className="text-slate-400 italic">({r.type})</span>}</span> },
    { key: "media_status", label: "Media", width: 110, render: (r) => {
      if (!r.has_media) return "";
      const I = ICON[r.type] || FileText;
      return r.media_status === "stored" && r.media ? <button className="inline-flex items-center gap-1 text-[#327de3] hover:underline" onClick={(e) => { e.stopPropagation(); setViewer(r.media); }} data-testid={`dl-activity-open-${r.id}`}><I size={13} /> view</button>
        : <span className="inline-flex items-center gap-1 text-amber-600 text-xs"><Warning size={12} /> {r.media_status}</span>;
    } },
    { key: "ai", label: "Work label", width: 280, render: (r) => r.media ? <span title={r.media.ai_description || ""}>{r.media.label_task || r.media.ai_short || (r.media.ai_status === "done" ? "" : "analyzing…")}{r.media.label_site ? ` · ${r.media.label_site}${r.media.label_unit ? ` U${r.media.label_unit}` : ""}` : ""}</span> : "" },
  ], []);
  return (
    <div className="space-y-3" data-testid="dl-activity">
      {viewer && <DlMediaViewer items={[viewer]} index={0} onClose={() => setViewer(null)} />}
      <div className="flex flex-wrap gap-2 items-center">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder='Search messages — e.g. "door frame unit 12"' className="h-9 max-w-md" data-testid="dl-activity-search" />
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-9 w-40" data-testid="dl-activity-from" />
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-9 w-40" data-testid="dl-activity-to" />
        <span className="text-xs text-slate-500">{rows.length} messages</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <ExplorerTable prefKey="dailylogs.activity" testId="dl-activity-table" columns={columns} rows={rows} defaultSort={{ key: "ts", dir: "desc" }} emptyText="No archived messages yet. Connect WhatsApp and enable a group." />
      </div>
    </div>
  );
}

const fsel = "h-8 rounded-md border border-slate-200 bg-white px-2 text-xs max-w-[220px]";

export function MediaTab() {
  const r = useRange("yesterday");
  const [crew, setCrew] = useState("");
  const [site, setSite] = useState("");
  const [kind, setKind] = useState("");
  const [d, setD] = useState({ items: [], total: 0, photos: 0, videos: 0, crews: [], sites: [] });
  const [more, setMore] = useState(false);
  const [viewer, setViewer] = useState(null);
  const params = { date_from: r.from, date_to: r.to, crew: crew || undefined, site_id: site || undefined, kind: kind || undefined };
  useEffect(() => {
    if (!r.from || !r.to || r.to < r.from) return;
    api.get("/dailylogs/media", { params: { ...params, offset: 0 } }).then(({ data }) => setD(data)).catch(() => setD((x) => ({ ...x, items: [], total: 0 })));
  }, [r.from, r.to, crew, site, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadMore = async () => {
    setMore(true);
    try { const { data } = await api.get("/dailylogs/media", { params: { ...params, offset: d.items.length } }); setD((x) => ({ ...data, items: [...x.items, ...data.items] })); } finally { setMore(false); }
  };
  const active = crew || site || kind || r.mode !== "yesterday";
  const clear = () => { setCrew(""); setSite(""); setKind(""); r.pick("yesterday"); };
  return (
    <div className="space-y-3" data-testid="dl-media">
      <CoverageNotice from={r.from} to={r.to} />
      <div className="flex flex-wrap items-center gap-2"><RangePills {...r} /></div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={crew} onChange={(e) => setCrew(e.target.value)} className={fsel} data-testid="dl-media-crew"><option value="">All crews</option>{d.crews.map((c) => <option key={c} value={c}>{c}</option>)}</select>
        <select value={site} onChange={(e) => setSite(e.target.value)} className={fsel} data-testid="dl-media-site"><option value="">All sites</option>{d.sites.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}<option value="none">Site unconfirmed</option></select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} className={fsel} data-testid="dl-media-kind"><option value="">Photos & videos</option><option value="image">Photos</option><option value="video">Videos</option><option value="audio">Voice notes</option><option value="document">Documents</option></select>
        {active && <Button size="sm" variant="ghost" onClick={clear} className="h-8 text-xs" data-testid="dl-media-clear">Clear filters</Button>}
        <span className="text-xs text-slate-500" data-testid="dl-media-count">{d.total} items · {d.photos} photos · {d.videos} videos · {r.from === r.to ? r.from : `${r.from} → ${r.to}`}</span>
      </div>
      {d.items.length === 0 && <div className="text-sm text-slate-500 py-8 text-center" data-testid="dl-media-empty">No media matches these filters.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {d.items.map((m, i) => {
          const I = ICON[m.kind] || FileText;
          return (
            <div key={m.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid={`dl-media-${m.id}`}>
              {m.thumb_url ? <Thumb m={m} className="w-full aspect-[4/3] rounded-none" onClick={() => setViewer(i)} />
                : <button onClick={() => setViewer(i)} className="w-full aspect-[4/3] bg-slate-100 flex items-center justify-center"><I size={30} className="text-slate-400" /></button>}
              <div className="px-2 py-1.5 space-y-0.5">
                <div className="text-[11px] font-semibold text-slate-800 truncate" title={m.display_name}>{m.label_task || m.ai_short || m.display_name || "Analyzing…"}</div>
                <div className="text-[10px] text-slate-500 truncate">{m.label_site || "Site unconfirmed"}{m.label_unit ? ` · Unit ${m.label_unit}` : ""} · {m.employee || m.sender_name}</div>
                <div className="text-[10px] text-slate-400 truncate">{fmt(m.sent_at)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {d.items.length < d.total && <div className="text-center"><Button size="sm" variant="outline" onClick={loadMore} disabled={more} data-testid="dl-media-more">{more ? "Loading…" : `Load more (${d.total - d.items.length})`}</Button></div>}
      {viewer != null && <DlMediaViewer items={d.items} index={viewer} onClose={() => setViewer(null)} />}
    </div>
  );
}
